'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UISection from 'UIComponents/section/UISection';
import Review from 'marketplace-ui-reviews/records/Review';
import Rating from 'marketplace-ui-reviews/components/Rating';
import ReviewBody from 'marketplace-ui-reviews/components/ReviewBody';
import ReviewerInfo from 'marketplace-ui-reviews/components/ReviewerInfo';
import ReviewReplyThread from 'marketplace-ui-reviews/components/ReviewReplyThread';
import ReviewActionsWrapper from 'marketplace-ui-reviews/components/ReviewActionsWrapper';
import HelpfulButton from 'marketplace-ui-reviews/components/HelpfulButton';
import ReportButton from 'marketplace-ui-reviews/components/ReportButton';
import ReportReviewModal from 'marketplace-ui-reviews/components/modals/ReportReviewModal';
import { Reportables } from 'marketplace-ui-reviews/constants/ReportReasons';
import { RatingAxes } from 'marketplace-ui-reviews/constants/RatingAxes';
import MediaSize from 'ecosystem-marketplace-core/components/layout/MediaSize';
import { getIsCustomer, getUserId } from '../../selectors/auth';
import { reportProfileReply, reportProfileReview, toggleReviewHelpful } from '../../actions/ReviewActions';
import { makeGetUserMarkedHelpfulSelector } from '../../selectors/reviews';
import { getProfileReviewability } from '../../selectors/profile';
import { getServices } from '../../slices/serviceCatalogSlice';

const ProfileReviewRenderer = ({
  review,
  searchTerm
}) => {
  const {
    createdAt,
    reply
  } = review;
  const [reportReviewModalOpen, setReportReviewModalOpen] = useState(false);
  const [reportReplyModalOpen, setReportReplyModalOpen] = useState(false);
  const isCustomer = useSelector(getIsCustomer);
  const userId = useSelector(getUserId);
  const getUserMarkedHelpful = useMemo(makeGetUserMarkedHelpfulSelector, []);
  const userMarkedHelpful = useSelector(state => getUserMarkedHelpful(state, review.get('id')));
  const catalogServices = useSelector(getServices);
  const dispatch = useDispatch();
  const {
    hasProfile
  } = useSelector(getProfileReviewability);
  const handleClickHelpful = useCallback(() => {
    dispatch(toggleReviewHelpful(review.get('id'), userId, !userMarkedHelpful));
  }, [dispatch, review, userId, userMarkedHelpful]);
  const renderedReportReviewModal = useMemo(() => /*#__PURE__*/_jsx(ReportReviewModal, {
    catalogServices: catalogServices,
    review: review,
    type: Reportables.REVIEW,
    onClose: () => setReportReviewModalOpen(false),
    onConfirm: ({
      reason,
      comment
    }) => {
      dispatch(reportProfileReview({
        reviewId: review.get('id'),
        reportReason: reason,
        comment
      }));
      setReportReviewModalOpen(false);
    }
  }), [dispatch, review, catalogServices]);
  const renderedReportReplyModal = useMemo(() => /*#__PURE__*/_jsx(ReportReviewModal, {
    catalogServices: catalogServices,
    review: review,
    type: Reportables.REPLY,
    onClose: () => setReportReplyModalOpen(false),
    onConfirm: ({
      reason,
      comment
    }) => {
      dispatch(reportProfileReply({
        reviewId: review.get('id'),
        replyId: review.get('reply').get('id'),
        reportReason: reason,
        comment
      }));
      setReportReplyModalOpen(false);
    }
  }), [dispatch, review, catalogServices]);
  const reviewActions = useMemo(() => {
    const helpfulDisabled = hasProfile || !isCustomer;
    const helpfulTooltip = hasProfile ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystem-marketplace-solutions-ui.profile.reviews.helpful.isProvider"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "ecosystem-marketplace-solutions-ui.profile.reviews.helpful.isPublic"
    });
    return /*#__PURE__*/_jsxs(ReviewActionsWrapper, {
      children: [/*#__PURE__*/_jsx(HelpfulButton, {
        className: "m-right-2",
        disabled: helpfulDisabled,
        helpfulCount: review.getHelpfulCount(),
        onClickHelpful: handleClickHelpful,
        tooltip: helpfulTooltip,
        userMarkedHelpful: userMarkedHelpful
      }), /*#__PURE__*/_jsx(ReportButton, {
        className: "m-left-2",
        isCustomer: isCustomer,
        onClick: () => setReportReviewModalOpen(true)
      })]
    });
  }, [hasProfile, isCustomer, handleClickHelpful, review, userMarkedHelpful]);
  return /*#__PURE__*/_jsxs("div", {
    className: "p-y-8",
    style: {
      width: '100%',
      borderBottom: `1px solid ${GREAT_WHITE}`
    },
    children: [/*#__PURE__*/_jsx(UISection, {
      children: /*#__PURE__*/_jsx(Rating, {
        direction: "column",
        rating: review.get('ratings').get(RatingAxes.OVERALL),
        label: /*#__PURE__*/_jsx(FormattedDateTime, {
          value: createdAt,
          format: "ll",
          type: "userTz"
        }),
        showTextRating: true
      })
    }), /*#__PURE__*/_jsx(MediaSize, {
      minDeviceSize: "md",
      alternate: /*#__PURE__*/_jsx(UIExpandableText, {
        maxHeight: 330,
        hideButtonCaret: true,
        children: /*#__PURE__*/_jsx(ReviewBody, {
          review: review,
          searchTerm: searchTerm,
          catalogServices: [...catalogServices]
        })
      }),
      children: /*#__PURE__*/_jsx(ReviewBody, {
        review: review,
        searchTerm: searchTerm,
        catalogServices: catalogServices
      })
    }), /*#__PURE__*/_jsx(ReviewerInfo, {
      review: review,
      inline: true
    }), reviewActions, /*#__PURE__*/_jsx(ReviewReplyThread, {
      reply: reply,
      onReportReplyClick: isCustomer ? () => setReportReplyModalOpen(true) : undefined
    }), reportReviewModalOpen && renderedReportReviewModal, reportReplyModalOpen && renderedReportReplyModal]
  });
};

ProfileReviewRenderer.propTypes = {
  review: PropTypes.instanceOf(Review).isRequired,
  searchTerm: PropTypes.string
};
export default ProfileReviewRenderer;